import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, FileText, MessageSquare, Star, HelpCircle, LogOut } from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const menuItems = [
    {
      title: "Manage Properties",
      description: "Add, edit, or remove property listings",
      icon: Home,
      path: "/admin/properties",
      color: "bg-blue-500"
    },
    {
      title: "View Inquiries",
      description: "See contact form submissions from visitors",
      icon: MessageSquare,
      path: "/admin/inquiries",
      color: "bg-green-500"
    },
    {
      title: "Manage Testimonials",
      description: "Add or remove client testimonials",
      icon: Star,
      path: "/admin/testimonials",
      color: "bg-yellow-500"
    },
    {
      title: "Manage FAQs",
      description: "Add or edit frequently asked questions",
      icon: HelpCircle,
      path: "/admin/faqs",
      color: "bg-purple-500"
    }
  ];

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
      <div className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div>
            <span className="text-2xl font-heading font-bold">MURRAY</span>
            <span className="text-2xl font-heading font-light text-accent ml-2">REALTOR</span>
            <span className="ml-4 text-sm uppercase tracking-widest">Admin Panel</span>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-primary h-10 px-6 rounded-none uppercase tracking-widest text-sm"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary mb-4">Admin Dashboard</h1>
          <p className="text-lg text-muted-foreground">Welcome back, Paul. Manage your website content from here.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`menu-${item.title.toLowerCase().replace(' ', '-')}`}>
                <CardHeader>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 ${item.color} flex items-center justify-center mr-4`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{item.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Link to="/">
                <Button variant="outline" data-testid="view-website-button">
                  <FileText className="w-4 h-4 mr-2" />
                  View Public Website
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;